import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  IconButton,
  Grid,
  Chip,
  CircularProgress,
} from '@mui/material';
import School from '@mui/icons-material/School';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import ArrowBack from '@mui/icons-material/ArrowBack';
import PersonAdd from '@mui/icons-material/PersonAdd';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../contexts/AuthContext';
import { mockStudents } from '../../mock';

const registrationSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Phone number must be at least 10 digits'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
  studentIds: z.array(z.object({
    id: z.string().min(1, 'Student ID is required'),
  })).min(1, 'At least one student ID is required'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type RegistrationFormData = z.infer<typeof registrationSchema>;

interface ParentRegistrationProps {
  onRegistrationSuccess: () => void;
  onBackToLogin: () => void;
}

export const ParentRegistration: React.FC<ParentRegistrationProps> = ({
  onRegistrationSuccess,
  onBackToLogin,
}) => {
  const { register: registerParent } = useAuth();
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [verifiedStudents, setVerifiedStudents] = useState<Map<string, { firstName: string; lastName: string }>>(new Map());

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegistrationFormData>({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      studentIds: [{ id: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'studentIds',
  });

  const studentIds = watch('studentIds');

  const verifyStudentId = (index: number, studentId: string) => {
    if (!studentId) {
      const newVerified = new Map(verifiedStudents);
      newVerified.delete(studentIds[index]?.id || '');
      setVerifiedStudents(newVerified);
      return;
    }

    const student = mockStudents.find(s => s.id === studentId);
    const newVerified = new Map(verifiedStudents);

    if (student) {
      newVerified.set(studentId, { firstName: student.firstName, lastName: student.lastName });
    } else {
      newVerified.delete(studentId);
    }

    setVerifiedStudents(newVerified);
  };

  const onSubmit = async (data: RegistrationFormData) => {
    setError('');
    setLoading(true);

    try {
      const validStudentIds = data.studentIds.map(s => s.id);
      const invalidIds = validStudentIds.filter(id => !mockStudents.find(s => s.id === id));

      if (invalidIds.length > 0) {
        throw new Error(`Invalid student ID(s): ${invalidIds.join(', ')}`);
      }

      await registerParent(
        {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          studentIds: validStudentIds,
        },
        data.password
      );

      onRegistrationSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: '#f3f4f6',
        py: { xs: 2, sm: 4 },
        px: { xs: 2, sm: 0 },
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            overflow: 'hidden',
            borderRadius: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 50%, #0891b2 100%)',
              py: { xs: 4, sm: 5 },
              px: { xs: 3, sm: 4 },
              textAlign: 'center',
              color: 'white',
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: 64, sm: 80 },
                height: { xs: 64, sm: 80 },
                borderRadius: '50%',
                bgcolor: 'white',
                mb: { xs: 2, sm: 3 },
              }}
            >
              <PersonAdd sx={{ fontSize: { xs: 36, sm: 48 }, color: '#0f766e' }} />
            </Box>
            <Typography
              variant="h4"
              gutterBottom
              fontWeight={700}
              sx={{
                color: 'white',
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              Parent Registration
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }}
            >
              Create an account to manage your children's school fees
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 3, sm: 4 }, bgcolor: 'white' }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
                Parent Information
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="First Name"
                        fullWidth
                        error={!!errors.firstName}
                        helperText={errors.firstName?.message}
                        disabled={loading}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Last Name"
                        fullWidth
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message}
                        disabled={loading}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Email Address"
                        type="email"
                        fullWidth
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        disabled={loading}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Phone Number"
                        fullWidth
                        error={!!errors.phone}
                        helperText={errors.phone?.message}
                        disabled={loading}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Password"
                        type="password"
                        fullWidth
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        disabled={loading}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="confirmPassword"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Confirm Password"
                        type="password"
                        fullWidth
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword?.message}
                        disabled={loading}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
                Student IDs
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                Enter the student ID(s) for your child(ren). You can add multiple students.
              </Alert>

              {fields.map((field, index) => (
                <Box key={field.id} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Controller
                      name={`studentIds.${index}.id`}
                      control={control}
                      render={({ field: inputField }) => (
                        <TextField
                          {...inputField}
                          label={`Student ID ${index + 1}`}
                          fullWidth
                          error={!!errors.studentIds?.[index]?.id}
                          helperText={errors.studentIds?.[index]?.id?.message}
                          disabled={loading}
                          onBlur={() => verifyStudentId(index, inputField.value)}
                        />
                      )}
                    />
                    {fields.length > 1 && (
                      <IconButton
                        color="error"
                        onClick={() => {
                          const studentId = studentIds[index]?.id;
                          if (studentId) {
                            const newVerified = new Map(verifiedStudents);
                            newVerified.delete(studentId);
                            setVerifiedStudents(newVerified);
                          }
                          remove(index);
                        }}
                        disabled={loading}
                        sx={{ mt: 1 }}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Box>
                  {studentIds[index]?.id && verifiedStudents.has(studentIds[index].id) && (
                    <Chip
                      label={`${verifiedStudents.get(studentIds[index].id)?.firstName} ${verifiedStudents.get(studentIds[index].id)?.lastName}`}
                      color="success"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}
                  {studentIds[index]?.id && !verifiedStudents.has(studentIds[index].id) && (
                    <Chip
                      label="Student ID not found"
                      color="error"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              ))}

              {errors.studentIds && typeof errors.studentIds === 'object' && 'message' in errors.studentIds && (
                <Typography color="error" variant="caption" sx={{ display: 'block', mb: 2 }}>
                  {errors.studentIds.message}
                </Typography>
              )}

              <Button
                startIcon={<Add />}
                onClick={() => append({ id: '' })}
                disabled={loading}
                sx={{ mb: 3 }}
              >
                Add Another Student
              </Button>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Create Account'}
                </Button>
              </Box>

              <Button
                startIcon={<ArrowBack />}
                onClick={onBackToLogin}
                fullWidth
                sx={{ mt: 2 }}
                disabled={loading}
              >
                Back to Login
              </Button>
            </form>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
